import React, { useEffect, useState } from 'react';
import { WaitlistCTA } from './WaitlistCTA';
const BACKGROUND_VIDEO_URL = 'https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260423_084718_72a17915-4964-4059-afcd-22d59399b72e.mp4';
const HERO_LINES = ['Your team and AI', 'in the same room.'];
function useReducedMotionPreference() {
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updatePreference = () => setReducedMotion(mediaQuery.matches);
    updatePreference();
    mediaQuery.addEventListener('change', updatePreference);
    return () => mediaQuery.removeEventListener('change', updatePreference);
  }, []);
  return reducedMotion;
}
function AnimatedHeading({
  lines,
  reducedMotion
}: {
  lines: string[];
  reducedMotion: boolean;
}) {
  const [started, setStarted] = useState(false);
  useEffect(() => {
    if (reducedMotion) {
      setStarted(true);
      return;
    }
    const timeout = window.setTimeout(() => setStarted(true), 120);
    return () => window.clearTimeout(timeout);
  }, [reducedMotion]);
  return <h1 aria-label={lines.join(' ')} className="text-5xl font-normal leading-[0.94] tracking-[-0.055em] text-white sm:text-6xl md:text-7xl lg:text-8xl" data-id="element-5991" data-mp="30i5pq0qjztrq">
      {lines.map((line, index) => <span key={line} aria-hidden="true" className="block overflow-hidden pb-[0.06em]" data-id="element-5992" data-mp="39iawq9ru4q5d" data-mpi={index}>
          <span className="block transition-[opacity,transform] duration-700 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] font-[500]" style={{
        opacity: started ? 1 : 0,
        transform: started || reducedMotion ? 'translateY(0)' : 'translateY(0.34em)',
        transitionDelay: reducedMotion ? '0ms' : `${index * 90}ms`
      }} data-id="element-5993" data-mp="3ef18wm0ir1li">
            {line}
          </span>
        </span>)}
    </h1>;
}
function FadeIn({
  children,
  delay,
  duration = 800,
  reducedMotion,
  className = ''
}: {
  children: React.ReactNode;
  delay: number;
  duration?: number;
  reducedMotion: boolean;
  className?: string;
}) {
  const [visible, setVisible] = useState(false);
  const isVisible = reducedMotion || visible;
  useEffect(() => {
    if (reducedMotion) {
      setVisible(true);
      return;
    }
    const timeout = window.setTimeout(() => setVisible(true), delay);
    return () => window.clearTimeout(timeout);
  }, [delay, reducedMotion]);
  return <div className={`transition-[opacity,transform] [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] ${className}`} style={{
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0)' : 'translateY(10px)',
    transitionDuration: reducedMotion ? '0ms' : `${duration}ms`
  }} data-id="element-5994" data-mp="2kt41dbnfv33y">
      {children}
    </div>;
}
function GlassFrame({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={`rounded-2xl p-px shadow-[inset_0_1px_1px_rgba(255,255,255,0.12),0_18px_48px_rgba(20,12,8,0.28)] ${className}`} style={{
    background: 'linear-gradient(180deg, rgba(255,222,198,0.52) 0%, rgba(234,88,12,0.28) 38%, rgba(255,222,198,0.18) 100%)'
  }} data-id="element-5995" data-mp="10e25pttvxjjh">
      <div className="rounded-[15px] bg-[#2B211A]/75 backdrop-blur-md" data-id="element-5996" data-mp="3c03znycertb3">
        {children}
      </div>
    </div>;
}
export function TeamWorkspaceHero() {
  const reducedMotion = useReducedMotionPreference();
  return <section className="relative isolate flex min-h-[100svh] w-full overflow-hidden bg-[#2B211A] text-white" style={{
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif"
  }} data-id="element-5997" data-mp="32a15vvxgqbbp">
      <video autoPlay={!reducedMotion} loop muted playsInline preload={reducedMotion ? 'metadata' : 'auto'} aria-hidden="true" className="absolute inset-0 -z-30 h-full w-full object-cover object-center" data-id="element-5998" data-mp="2tbfhhk5b8230">
        <source src={BACKGROUND_VIDEO_URL} type="video/mp4" data-id="element-5999" data-mp="2jd9hj832d60s" />
      </video>

      {/* Colorize the white glows in the video to orange */}
      <div aria-hidden="true" className="absolute inset-0 -z-20 bg-[#EA580C] mix-blend-color opacity-60" data-id="element-6000" data-mp="ck7do1r5eax0" />

      <div aria-hidden="true" className="absolute inset-0 -z-20 bg-[#2B211A]/70" data-id="element-6001" data-mp="2do9htegi0t15" />
      <div aria-hidden="true" className="absolute inset-0 -z-10 opacity-25" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='8' height='8' viewBox='0 0 8 8' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='2' height='2' fill='%23EA580C' fill-opacity='0.42'/%3E%3C/svg%3E\")",
      backgroundSize: '8px 8px'
    }} data-id="element-6002" data-mp="2dm5eoyqfby1x" />

      <div className="relative z-10 flex min-h-[100svh] w-full flex-col items-center justify-center px-6 pb-36 pt-32 text-center sm:px-10 md:px-12 md:pb-40 md:pt-36 lg:px-16" data-id="element-6003" data-mp="1upnh40ow93xr">
        <div className="flex w-full max-w-5xl flex-col items-center" data-id="element-6004" data-mp="1lwtzvxpvzuh9">
          <AnimatedHeading lines={HERO_LINES} reducedMotion={reducedMotion} data-id="element-6005" data-mp="1luzdeeyyvub2" />

          <FadeIn delay={440} duration={700} reducedMotion={reducedMotion} className="mt-6 max-w-2xl" data-id="element-6006" data-mp="1gzk13rgly6gr">
            <p className="text-base leading-relaxed text-[#F8E8DD] sm:text-lg md:text-xl" data-id="element-6007" data-mp="1bye43hfwzx1f">
              Giga turns the context scattered across your team&apos;s tools and
              conversations into decisions everyone can see, trust, and act on.
            </p>
          </FadeIn>

          <FadeIn delay={580} duration={700} reducedMotion={reducedMotion} className="mt-7 flex flex-wrap justify-center gap-3 sm:mt-8 sm:gap-4" data-id="element-6008" data-mp="11tnyn1layasg">
            <WaitlistCTA analyticsLabel="team-workspace-video-hero" className="rounded-full bg-[#EA580C] px-8 py-3.5 text-sm font-bold text-white shadow-[0_12px_28px_rgba(115,39,6,0.38)] transition-colors duration-200 hover:bg-[#D24E09] sm:px-9" data-id="element-6009" data-mp="1bt53jgsqcknd">
              Get Giga for your team
            </WaitlistCTA>
            <a href="/demo" className="rounded-full border border-white/35 bg-[#2B211A]/45 px-8 py-3.5 text-sm font-bold text-white backdrop-blur-md transition-colors duration-200 hover:bg-white hover:text-[#2B211A] sm:px-9" data-id="element-6010" data-mp="o3uaklhmfujs">
              See Giga in action
            </a>
          </FadeIn>
        </div>
      </div>

      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex justify-center px-5 pb-8 sm:px-8 md:px-12 md:pb-12 lg:px-16 lg:pb-16" data-id="element-6011" data-mp="d6omwavf4e8">
        <FadeIn delay={1400} duration={1000} reducedMotion={reducedMotion} data-id="element-6012" data-mp="3oglhxtngl3ie">
          <GlassFrame className="pointer-events-auto max-w-full" data-id="element-6013" data-mp="3jj24ux2hyws1">
            <div className="flex items-center justify-center px-6 py-3 sm:px-8" data-id="element-6014" data-mp="23d83yzr7043">
              <p className="text-sm font-light text-white sm:text-base lg:text-lg" data-id="element-6015" data-mp="3t9ohqjitx971">
                Shared context. Clear decisions. Better momentum.
              </p>
            </div>
          </GlassFrame>
        </FadeIn>
      </div>
    </section>;
}